import { mainLogger as log } from "@/app/logger"
import { LZString } from "@/app/lz-string/main"
import { IS_SERVER } from "@/app/types"
import { isNotNull } from "@/app/utils"

interface Storage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
  clear(): void
  length: number
  key(index: number): string | null
}

type StorageType = "localStorage" | "sessionStorage"
type StoreItem = { ts: number; expireAt: number | null; data: any }

function getStorage(storageType: StorageType): Storage | null {
  if (IS_SERVER()) return null // on server
  if (storageType !== "localStorage" && storageType !== "sessionStorage")
    return null
  try {
    const storage = window[storageType] as Storage | undefined
    if (!storage) return null
    const x = "__storage_test__"
    storage.setItem(x, x)
    storage.removeItem(x)
    return storage
  } catch (err) {
    log.error(`Got error while checking ${storageType} availability, err:`, err)
    return null
  }
}

class _StorageService {
  private storage: Storage | null

  constructor(private storageType: StorageType) {
    this.storageType = storageType
    this.storage = getStorage(this.storageType)
  }

  isAvailable(): boolean {
    return this.storage != null
  }

  // set() will return the data if it saved successfully or null otherwise
  set(
    key: string,
    data: any,
    expireAt: number | null = null,
    compressData: boolean = true
  ): any {
    if (!this.isAvailable()) return null
    if (typeof key !== "string")
      throw new Error(`Storage key must be string. Got:${typeof key}`)
    log.debug("STORAGE Setting ", { key, data, expireAt })

    try {
      const item: StoreItem = { ts: Date.now(), data, expireAt }
      let json: string = JSON.stringify(item)

      if (compressData) {
        const compressed_json: string = LZString.compressToUTF16(json)
        log.debug("STORAGE compressed", {
          key,
          original: json.length,
          compressed: compressed_json.length,
        })
        json = compressed_json
      }

      this.storage.setItem(key, json)
      return data
    } catch (err) {
      log.error(
        "Error in setting data for key:",
        key,
        "in",
        this.storageType,
        ", err:",
        err
      )
      // ignore and return null
    }
    return null
  }

  // internal get() to read from Storage, use other public getters
  private _get(key: string, compressData: boolean = true): StoreItem | null {
    try {
      const serializedData =
        this.isAvailable() ? this.storage.getItem(key) : null
      if (!serializedData) return null

      let json: string = serializedData

      if (compressData) {
        json = LZString.decompressFromUTF16(serializedData)
      }
      const item: StoreItem = JSON.parse(json) as StoreItem
      log.debug(
        "STORAGE Returning data for",
        key,
        "saved at",
        new Date(item.ts).toLocaleString(),
        "expiring at",
        new Date(item.expireAt).toLocaleString()
      )
      return item
    } catch (err) {
      log.error(
        "Error in reading key:",
        key,
        "from",
        this.storageType,
        "err:",
        err
      )
      // ignore and return null
    }
    return null
  }

  getSavedAt(key: string, compressData: boolean = true): number | null {
    const item = this._get(key, compressData)
    return isNotNull(item) ? item.ts : null
  }
  getExpireAt(key: string, compressData: boolean = true): number | null {
    const item = this._get(key)
    return isNotNull(item) ? item.expireAt : null
  }
  get(key: string, compressData: boolean = true): any {
    const item = this._get(key, compressData)
    // check if it's expired
    if (item?.expireAt && Date.now() > item.expireAt) {
      this.storage.removeItem(key)
      return null
    }
    return isNotNull(item) ? item.data : null
  }

  // returns the key for which the testFn(key, idx) returns true
  // similar to Array.find(), see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/find
  findKey(testFn: (key: string, idx?: number) => boolean): string | null {
    try {
      if (!this.isAvailable()) return null
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i)
        if (testFn(key, i)) return key
      }
    } catch (err) {
      log.error("Error in finding key:", "in", this.storageType, "err:", err)
      // ignore and return null
    }
    return null
  }

  findAllKeys(): string[] | null {
    try {
      if (!this.isAvailable()) return null
      const keys: string[] = []
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i)
        keys.push(key)
      }
      return keys
    } catch (err) {
      log.error("Error in finding keys:", "in", this.storageType, "err:", err)
    }
    return null
  }

  remove(key: string, compressData: boolean = true): any {
    try {
      const item: StoreItem = this._get(key, compressData)
      if (item) {
        this.storage.removeItem(key)
        return item.data
      }
    } catch (err) {
      log.error(
        "Error in removing key:",
        key,
        "from",
        this.storageType,
        "err:",
        err
      )
      // ignore and return null
    }
    return null
  }

  clear() {
    try {
      if (this.isAvailable()) this.storage.clear()
    } catch (err) {
      log.error("Error in clearing:", this.storageType, "err:", err)
      // ignore and continue
    }
  }
}

export const LocalStorage = new _StorageService("localStorage")
export const SessionStorage = new _StorageService("sessionStorage")
